import * as Sentry from '@sentry/react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementLocale } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { KYCStatus } from '~/api/KYCStatus';
import { PaymentSystem } from '~/api/Order';
import { company } from '~/company/Company';
import { ModalSlotsChangedEvent } from '~/components/ModalSlotsChangedEvent';
import { ModalSlotsChangedEventCD } from '~/components/ModalSlotsChangedEventCD';
import { useModal } from '~/hooks/useModal';
import CheckoutContent from '~/pages/Checkout/CheckoutContent';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import { DeliveryServiceVersion } from '~/stores/DeliveryStore/interfaces';
import { ApplyPromocodeCallerSource } from '~/stores/PromotionsStore/interfaces';
import { getAddressModalParams } from '~/utils/addressModal';

import CheckoutContentCD from './CheckoutContentCD';
import CheckoutSkeleton from './CheckoutSkeleton';
import CheckoutSkeletonCD from './components/SkeletonCD/CheckoutSkeletonCD';

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [isReady, setIsReady] = useState(false);
  const initialDeliveryZoneIdRef = useRef(
    orderStore.etaCalculation?.warehouse.zone[checkoutStore.deliveryMethod]?.id,
  );
  const analyticsTrackedRef = useRef(false);

  const init = useCallback(async () => {
    if (
      cartStore.promotionStore.promocode.value &&
      !cartStore.promotionStore.promocode.coupon
    ) {
      cartStore.promotionStore
        .applyPromocode(
          cartStore.promotionStore.promocode.value,
          ApplyPromocodeCallerSource.CHECKOUT,
        )
        .then((isPromocodeApplied) => {
          if (!isPromocodeApplied) {
            throw new Error('Promocode is not applied');
          }
        })
        .catch(() => {
          mainStore.setIsInvalidPromocodePopover(true);
        });
    }

    try {
      checkoutStore.setIsUpdatedDeliveryPopover(false);
      // calculateCart() used to be called here explicitly. After the move to
      // mobx-tanstack-query, calculateQuery refetches automatically on any
      // change in cartHash / calculateDepsHash (address, warehouse, zone,
      // deliveryMethod, deliveries, promocode, tips). A manual refetch here
      // produces an extra /calculate request with a different Network
      // initiator on every Checkout mount and every time init() re-runs via
      // deps (paymentSystems, deliveryAddress, availability.availableNow).
      await userStore.requestPersonalData();

      checkoutStore.setPhoneVal(userStore.personalData.phone ?? '');
      checkoutStore.setNameVal(userStore.fullName);
      checkoutStore.setEmailVal(userStore.personalData.email ?? '');
      checkoutStore.initStateData();
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          error,
        });
        Sentry.captureMessage('[Checkout] Failed to init', 'error');
      });
      checkoutStore.setNameVal('');
      checkoutStore.setEmailVal('');
    }

    if (!analyticsTrackedRef.current) {
      analyticsTrackedRef.current = true;

      analyticsService.track(AnalyticsEvent.PIXEL_INITIATE_CHECKOUT, {
        cart_id: undefined,
        products_amount: cartStore.cart.reduce(
          (sum, item) => sum + item.count,
          0,
        ),
        items_amount: cartStore.cart.length,
        price: cartStore.totalCartPrice.base,
        final_price: cartStore.finalPrice,
        eta_min: orderStore.etaCalculation?.duration.min || 0,
        eta_max: orderStore.etaCalculation?.duration.max || 0,
        delivery_fee: orderStore.fee.shippingPounds || 0,
        threshold: orderStore.fee.thresholdPounds || 0,
        is_surger: orderStore.etaCalculation?.highDemand || false,
      });

      analyticsService.track(AnalyticsEvent.PURCHASE_START_CHECKOUT, {
        cart_id: undefined,
        products_amount: cartStore.cart.reduce(
          (sum, item) => sum + item.count,
          0,
        ),
        items_amount: cartStore.cart.length,
        price: cartStore.totalCartPrice.base,
        final_price: cartStore.finalPrice,
        eta_min: orderStore.etaCalculation?.duration.min || 0,
        eta_max: orderStore.etaCalculation?.duration.max || 0,
        delivery_fee: orderStore.fee.shippingPounds || 0,
        threshold: orderStore.fee.thresholdPounds || 0,
        is_surger: orderStore.etaCalculation?.highDemand || false,
      });

      analyticsService.track(AnalyticsEvent.BEGIN_CHECKOUT, {
        currency: orderStore.currency.toUpperCase(),
        value: mainStore.toFloat(cartStore.finalPrice),
        items: cartStore.cartForFirebase,
        coupon: cartStore.promotionStore.promocode.value,
      });
    }

    setIsReady(true);
  }, [cartStore.promotionStore.promocode.value]);

  useEffect(() => {
    cartStore.enableCalculate();
    checkoutStore.resetStore({
      resetDeliveryMethod:
        orderStore.deliveryStore.version === DeliveryServiceVersion.ONE,
    });
    userStore.loyaltyPointsStore.getLoyaltyUserBalanceAndTransactions();
    const deliveryZoneId =
      orderStore.etaCalculation?.warehouse.zone[checkoutStore.deliveryMethod]
        ?.id;
    orderStore.requestPaymentSystems(deliveryZoneId);
    return () => cartStore.disableCalculate();
  }, []);

  useEffect(() => {
    if (checkoutStore.activePaymentSystem != null) {
      checkoutStore.requestPayments();
    }
  }, [checkoutStore.activePaymentSystem]);

  useEffect(() => {
    const currentDeliveryZoneId =
      orderStore.etaCalculation?.warehouse.zone[checkoutStore.deliveryMethod]
        ?.id;
    if (
      currentDeliveryZoneId &&
      initialDeliveryZoneIdRef.current !== currentDeliveryZoneId
    ) {
      initialDeliveryZoneIdRef.current = currentDeliveryZoneId;
      checkoutStore.requestPayments();
    }
  }, [orderStore.etaCalculation?.warehouse.zone, checkoutStore.deliveryMethod]);

  useEffect(() => {
    const initStripe = async () => {
      if (
        checkoutStore.activePaymentSystem === PaymentSystem.stripe &&
        company.envVariables.REACT_APP_STRIPE_API_KEY
      ) {
        const stripeInstance = await loadStripe(
          company.envVariables.REACT_APP_STRIPE_API_KEY,
          { locale: 'en-GB' },
        );

        if (!stripeInstance) {
          mainStore.pushAlert('error', t('errors:paymentSystemNotLoaded'));
          throw new Error('Something went wrong');
        }
        setStripe(stripeInstance);
      } else {
        // Clear stripe instance if we're not using Stripe
        setStripe(null);
      }
    };

    initStripe();
  }, [checkoutStore.activePaymentSystem]);

  useEffect(() => {
    if (
      !userStore.deliveryAddress ||
      !orderStore.etaCalculation?.warehouse.availability.availableNow
    ) {
      const [targetModal, source] = getAddressModalParams('Checkout');

      openModal(targetModal, source);
      return;
    }

    if (!cartStore.cart.length) {
      navigate('/cart', { replace: true });
      return;
    }

    if (!orderStore.paymentSystems.size) {
      Sentry.withScope((scope) => {
        scope.setExtras({
          error: 'Payment system is undefined',
        });
        Sentry.captureMessage('[Checkout] Initializing skipped', 'error');
      });
      return;
    }

    init();
  }, [
    orderStore.paymentSystems,
    userStore.deliveryAddress,
    orderStore.etaCalculation?.warehouse.availability.availableNow,
  ]);

  useEffect(() => {
    if (
      company.needVerification &&
      userStore.personalData.kycStatus !== KYCStatus.Verified
    ) {
      navigate('/cabinet/personal-data', {
        state: { from: 'cart' },
      });
    }
  }, [userStore.personalData.kycStatus]);

  useEffect(() => {
    cartStore.promotionStore.loadStoredPromoCodes();

    return () => {
      userStore.loyaltyPointsStore.setUseLoyaltyPoints(false);
    };
  }, []);

  if (!isReady) {
    return company.match({
      citydrinks: <CheckoutSkeletonCD />,
      v3: <CheckoutSkeletonCD />,
      default: <CheckoutSkeleton />,
    });
  }

  return (
    <>
      <Elements
        stripe={
          checkoutStore.activePaymentSystem === PaymentSystem.stripe
            ? stripe
            : null
        }
        options={{ locale: (userStore.lang ?? 'en') as StripeElementLocale }}
      >
        {company.match({
          citydrinks: <CheckoutContentCD />,
          v3: <CheckoutContentCD />,
          default: <CheckoutContent />,
        })}
      </Elements>

      <ModalSlotsChangedEventComponent />
    </>
  );
};

const ModalSlotsChangedEventComponent = company.match({
  citydrinks: ModalSlotsChangedEventCD,
  v3: ModalSlotsChangedEventCD,
  default: ModalSlotsChangedEvent,
});

export default observer(Checkout);
